import { QMS_CATEGORY_LABELS } from '../../constants/labels';
import type { QmsCategory } from '../../types/qmsCategoryTypes';

interface ViewQmsCategoryContentProps {
  category: QmsCategory | undefined;
  formattedDescription: string;
  formattedCreatedAt: string;
  formattedUpdatedAt: string;
}

const ViewQmsCategoryContent = ({
  category,
  formattedDescription,
  formattedCreatedAt,
  formattedUpdatedAt,
}: ViewQmsCategoryContentProps) => {
  const createdBy = category?.createdBy?.trim() ? category.createdBy : '—';
  const updatedBy = category?.updatedBy?.trim() ? category.updatedBy : '—';

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border-[1px] border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          aria-label={QMS_CATEGORY_LABELS.SECTION_DETAILS}
          data-testid="view-category-details-title"
        >
          {QMS_CATEGORY_LABELS.SECTION_DETAILS}
        </p>
        <div className="flex flex-col gap-8">
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid={QMS_CATEGORY_LABELS.FIELD_DESCRIPTION}
            >
              {QMS_CATEGORY_LABELS.FIELD_DESCRIPTION}
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid={`${QMS_CATEGORY_LABELS.FIELD_DESCRIPTION}-value`}
            >
              {formattedDescription}
            </p>
          </div>

          <div
            className="grid gap-x-16 gap-y-8"
            style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
          >
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid={QMS_CATEGORY_LABELS.FIELD_CREATED_AT}
              >
                {QMS_CATEGORY_LABELS.FIELD_CREATED_AT}
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid={`${QMS_CATEGORY_LABELS.FIELD_CREATED_AT}-value`}
              >
                {formattedCreatedAt}
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid={QMS_CATEGORY_LABELS.FIELD_CREATED_BY}
              >
                {QMS_CATEGORY_LABELS.FIELD_CREATED_BY}
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid={`${QMS_CATEGORY_LABELS.FIELD_CREATED_BY}-value`}
              >
                {createdBy}
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid={QMS_CATEGORY_LABELS.FIELD_UPDATED_BY}
              >
                {QMS_CATEGORY_LABELS.FIELD_UPDATED_BY}
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid={`${QMS_CATEGORY_LABELS.FIELD_UPDATED_BY}-value`}
              >
                {updatedBy}
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid={QMS_CATEGORY_LABELS.FIELD_UPDATED_AT}
              >
                {QMS_CATEGORY_LABELS.FIELD_UPDATED_AT}
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid={`${QMS_CATEGORY_LABELS.FIELD_UPDATED_AT}-value`}
              >
                {formattedUpdatedAt}
              </p>
            </div>
          </div>

          {category?.tags && category.tags.length > 0 && (
            <div className="flex flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid={QMS_CATEGORY_LABELS.FIELD_TAGS}
              >
                {QMS_CATEGORY_LABELS.FIELD_TAGS}
              </h2>
              <div className="flex flex-wrap gap-2">
                {category.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center h-8 px-3 rounded-lg bg-information-90 text-information-30 text-sm font-medium leading-5 tracking-[0.1px]"
                    data-testid={`tag-${tag.id}`}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewQmsCategoryContent;
